import { useCallback, type JSX } from 'react';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { MenuPopup } from '../components/Menu';
import { useUi, useUiContext } from '../context';
import { FiltersDialog } from '../dialogs/filters/FiltersDialog';
import { AddSourceDialog } from '../dialogs/misc/AddSourceDialog';
import { CollectionsDialog } from '../dialogs/misc/CollectionsDialog';
import { EditStreamInfoDialog } from '../dialogs/misc/EditStreamInfoDialog';
import { AboutDialog, ShortcutsDialog } from '../dialogs/misc/HelpDialogs';
import { RecordingsDialog } from '../dialogs/misc/RecordingsDialog';
import { StreamingToolsDialog } from '../dialogs/misc/StreamingToolsDialog';
import { ConfirmDialog, PromptDialog } from '../dialogs/misc/SimpleDialogs';
import { TransitionPropertiesDialog } from '../dialogs/misc/TransitionPropertiesDialog';
import { PropertiesDialog } from '../dialogs/properties/PropertiesDialog';
import { SettingsDialog } from '../dialogs/settings/SettingsDialog';
import { TransformDialog } from '../dialogs/transform/TransformDialog';
import { AdvancedAudioDialog } from '../panels/mixer/AdvancedAudioDialog';
import type { OpenDialog } from '../uiStore';
import './shell.css';

function DialogSwitch({ dialog }: { dialog: OpenDialog }): JSX.Element {
  const { ui } = useUiContext();
  const { key } = dialog;
  const onClose = useCallback(() => ui.getState().closeDialog(key), [ui, key]);
  switch (dialog.type) {
    case 'properties':
      return <PropertiesDialog sourceId={dialog.sourceId} onClose={onClose} />;
    case 'filters':
      return <FiltersDialog owner={dialog.owner} onClose={onClose} />;
    case 'transform':
      return <TransformDialog sceneId={dialog.sceneId} itemId={dialog.itemId} onClose={onClose} />;
    case 'settings':
      return <SettingsDialog initialTab={dialog.tab} onClose={onClose} />;
    case 'recordings':
      return <RecordingsDialog onClose={onClose} />;
    case 'edit-stream-info':
      return <EditStreamInfoDialog onClose={onClose} />;
    case 'streaming-tools':
      return <StreamingToolsDialog onClose={onClose} />;
    case 'advanced-audio':
      return <AdvancedAudioDialog onClose={onClose} />;
    case 'shortcuts':
      return <ShortcutsDialog onClose={onClose} />;
    case 'about':
      return <AboutDialog onClose={onClose} />;
    case 'collections':
      return <CollectionsDialog onClose={onClose} />;
    case 'add-source':
      return <AddSourceDialog kind={dialog.kind} onClose={onClose} />;
    case 'transition-properties':
      return <TransitionPropertiesDialog transitionId={dialog.transitionId} onClose={onClose} />;
    case 'prompt':
      return <PromptDialog spec={dialog} onClose={onClose} />;
    case 'confirm':
      return <ConfirmDialog spec={dialog} onClose={onClose} />;
  }
}

/** A dialog that crashes is closed and reported instead of taking the app with it. */
export function DialogHost(): JSX.Element {
  const { ui, studio } = useUiContext();
  const dialogs = useUi((s) => s.dialogs);
  return (
    <>
      {dialogs.map((dialog) => (
        <ErrorBoundary
          key={dialog.key}
          name="This dialog"
          onError={() => {
            studio.notify('error', 'A dialog stopped working and was closed.');
            ui.getState().closeDialog(dialog.key);
          }}
        >
          <DialogSwitch dialog={dialog} />
        </ErrorBoundary>
      ))}
    </>
  );
}

export function MenuHost(): JSX.Element | null {
  const { ui } = useUiContext();
  const menu = useUi((s) => s.menu);
  const onClose = useCallback(() => ui.getState().closeMenu(), [ui]);
  if (!menu || menu.items.length === 0) return null;
  return <MenuPopup items={menu.items} x={menu.x} y={menu.y} onClose={onClose} />;
}
